import type { CategoryDto, ProductDto } from "../../../shared/api-types";
import { TRANSLATION_LANG_NAMES, type TranslationLang } from "../../../shared/constants";
import { flagSeverity, type ReviewFlag } from "../../../shared/import-merge";
import { normalizeForSearch } from "../../../shared/slug";

// What the product list says about each product (admin review 2026-11-02): one indicator per row instead of a pile
// of chips, in three weights. "publish" blocks publishing (the server's publish gate says the same); "attention"
// does not block but has a consequence for clients (unconfirmed allergens: guests see Meniova's estimate);
// "optional" is only a gap (photo, description, translation) and never looks like a problem.

export interface RowIssues {
  publish: string[];
  attention: string[];
  optional: string[];
}

/** Short names of the import questions (the editor shows the full explanation). */
const FLAG_SHORT: Record<ReviewFlag["code"], string> = {
  name_unclear: "Nume de verificat",
  price_ambiguous: "Preț ambiguu",
  price_missing: "Preț lipsă",
  possible_duplicate: "Posibil duplicat",
  portion_unclear: "Gramaj de verificat",
  price_unverified: "Preț de confirmat",
  import_new: "Nou la import",
  import_changed: "Modificat la import",
  import_missing: "Lipsește din import",
};

export function rowIssues(p: ProductDto, langs: readonly TranslationLang[] = []): RowIssues {
  const publish: string[] = [];
  for (const f of p.reviewFlags) if (flagSeverity(f) === "critical") publish.push(FLAG_SHORT[f.code]);
  if (!p.contentConfirmed && !publish.length) publish.push("Importat, neconfirmat");
  const attention: string[] = [];
  if (!p.allergensConfirmed)
    attention.push(p.allergensEstimated ? "Alergeni estimați, de confirmat" : "Alergeni neconfirmați");
  for (const f of p.reviewFlags) if (flagSeverity(f) !== "critical") attention.push(FLAG_SHORT[f.code]);
  if (p.aiRecipeUnverified) attention.push("Rețetă AI neverificată");
  const optional: string[] = [];
  if (!p.imageKey) optional.push("fotografie");
  if (!p.description) optional.push("descriere");
  for (const l of langs) {
    const t = p.translations[l];
    if (!t) optional.push(`traducere ${TRANSLATION_LANG_NAMES[l]}`);
    else if (t.status === "auto_unreviewed" || t.status === "needs_update")
      optional.push(`traducere ${TRANSLATION_LANG_NAMES[l]} de verificat`);
  }
  return { publish, attention, optional };
}

export const PRODUCT_FILTERS = [
  ["all", "Toate produsele"],
  ["publish", "Blochează publicarea"],
  ["allergens", "Alergeni neconfirmați"],
  ["estimated", "Cu alergeni estimați de Meniova"],
  ["frozen", "Din produs congelat"],
  ["off", "Indisponibile azi"],
  ["draft", "Modificate în ciornă"],
  ["optional", "Cu lipsuri opționale"],
] as const;
export type ProductFilter = (typeof PRODUCT_FILTERS)[number][0];

export const isProductFilter = (v: string | null): v is ProductFilter =>
  PRODUCT_FILTERS.some(([k]) => k === v);

export interface FilterContext {
  langs?: readonly TranslationLang[];
  /** Products added or changed in the draft since the last publication. */
  draftChanged?: ReadonlySet<string>;
}

export function matchesFilter(p: ProductDto, f: ProductFilter, ctx: FilterContext = {}): boolean {
  switch (f) {
    case "all":
      return true;
    case "publish":
      return rowIssues(p, ctx.langs).publish.length > 0;
    case "allergens":
      return !p.allergensConfirmed;
    case "estimated":
      return !p.allergensConfirmed && !!p.allergensEstimated;
    case "frozen":
      return p.frozen;
    case "off":
      return !p.available;
    case "draft":
      return ctx.draftChanged?.has(p.id) ?? false;
    case "optional":
      return rowIssues(p, ctx.langs).optional.length > 0;
  }
}

export function matchesQuery(p: ProductDto, q: string): boolean {
  if (!q) return true;
  return normalizeForSearch(p.name).includes(q) || normalizeForSearch(p.description).includes(q);
}

export interface CategoryBlock {
  category: CategoryDto;
  index: number;
  all: ProductDto[];
  items: ProductDto[];
}

/** Categories with their matching products; with a search, a filter or a category chosen, empty ones are left out. */
export function filterBlocks(
  categories: CategoryDto[],
  products: ProductDto[],
  opts: { query: string; filter: ProductFilter; categoryId: string | null } & FilterContext,
): CategoryBlock[] {
  const q = normalizeForSearch(opts.query);
  const narrowing = !!q || opts.filter !== "all";
  const byCat = new Map<string, ProductDto[]>();
  for (const p of products) {
    const list = byCat.get(p.categoryId);
    if (list) list.push(p);
    else byCat.set(p.categoryId, [p]);
  }
  return categories
    .map((category, index) => {
      const all = byCat.get(category.id) ?? [];
      const items = all.filter((p) => matchesQuery(p, q) && matchesFilter(p, opts.filter, opts));
      return { category, index, all, items };
    })
    .filter((b) => (!opts.categoryId || b.category.id === opts.categoryId) && (!narrowing || b.items.length));
}

/** Big menus open with their categories closed (a list of headings); small ones open in full. */
export function startsCollapsed(categories: number, products: number): boolean {
  return categories > 6 || products > 40;
}
